import { Dispatch, useCallback, useState } from "react";
import { OnDragEndResponder } from "react-beautiful-dnd";

import { useDocumentsActions } from "store/actions-context";
import { Document, DocumentSection } from "store/types";

import { ContentContextType } from "./content-context";

interface UseSectionsDragProps {
  sections: DocumentSection[];
  documentId: number;
  setDocument: Dispatch<React.SetStateAction<Document<true>>>;
}

interface Point {
  x: number;
  y: number;
}

export const useSectionsDrag = ({ sections, documentId, setDocument }: UseSectionsDragProps) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<Point>({ x: 0, y: 0 });
  const [selectionEnd, setSelectionEnd] = useState<Point>({ x: 0, y: 0 });
  const [intersectingSections, setIntersectingSections] = useState<number[]>([]);

  const { reorderDocumentSectionsAction } = useDocumentsActions();

  const reorderSections = useCallback<ContentContextType["reorderSections"]>(
    async ({ order, documentId }) => {
      await reorderDocumentSectionsAction({ documentId, order });

      setDocument((prev) => ({
        ...prev,
        sections: prev.sections
          .map((section) => {
            const updatedSection = order.find((item) => item.id === section.id);
            if (!updatedSection) return section;

            return {
              ...section,
              order: updatedSection.position
            };
          })
          .sort((sec1, sec2) => sec1.order - sec2.order)
      }));
    },
    [setDocument]
  );

  /**
   * Handles the drag and drop reordering of sections
   * If multiple sections are selected (intersectingSections), moves them as a group
   * Otherwise moves a single section
   */
  const handleDrag: OnDragEndResponder = useCallback(
    async ({ destination, source }) => {
      if (!destination) return;

      const sectionsCopy = Array.from(sections);
      const draggedSectionId = sectionsCopy[source.index].id;

      // If there are selected sections but dragged section is not among them,
      // treat it as a single section move and clear selection
      if (intersectingSections.length > 0 && !intersectingSections.includes(draggedSectionId)) {
        const [removed] = sectionsCopy.splice(source.index, 1);
        sectionsCopy.splice(destination.index, 0, removed);

        reorderSections({
          documentId,
          order: sectionsCopy.map((section, index) => ({
            id: section.id,
            position: index
          }))
        });

        setIntersectingSections([]); // Clear selection after reordering
        return;
      }

      // If we have selected sections and dragged section is among them,
      // move the entire selection as a group
      if (intersectingSections.length > 0) {
        // Get indices of selected sections
        const selectedIndices = sectionsCopy
          .map((section, index) => ({ index, id: section.id }))
          .filter((item) => intersectingSections.includes(item.id))
          .map((item) => item.index)
          .sort((a, b) => a - b);

        // Save selected sections
        const selectedSections = selectedIndices.map((index) => sectionsCopy[index]);

        // Remove selected sections from array (starting from end to preserve indices)
        for (let i = selectedIndices.length - 1; i >= 0; i--) {
          sectionsCopy.splice(selectedIndices[i], 1);
        }

        // Determine insertion position
        let insertIndex = destination.index;

        // Check movement direction
        const isMovingDown = destination.index > source.index;

        // Adjust insertion index accounting for removed elements
        const removedBeforeDestination = selectedIndices.filter((index) => index < destination.index).length;
        insertIndex -= removedBeforeDestination;

        // If moving down, add adjustment
        if (isMovingDown) {
          insertIndex += 1;
        }

        // Insert group of selected sections
        sectionsCopy.splice(insertIndex, 0, ...selectedSections);

        // Update order
        reorderSections({
          documentId,
          order: sectionsCopy.map((section, index) => ({
            id: section.id,
            position: index
          }))
        });
        return;
      }

      // Single item movement (when no selection)
      const [removed] = sectionsCopy.splice(source.index, 1);
      sectionsCopy.splice(destination.index, 0, removed);

      reorderSections({
        documentId,
        order: sectionsCopy.map((section, index) => ({
          id: section.id,
          position: index
        }))
      });
    },
    [documentId, sections, intersectingSections, reorderSections]
  );

  const handleDragStart = useCallback(
    ({ source }: { source: { index: number } }) => {
      const draggedSectionId = sections[source.index].id;
      if (!intersectingSections.includes(draggedSectionId)) {
        setIntersectingSections([]);
      }
    },
    [sections, intersectingSections]
  );

  /**
   * Handles mouse down events for section selection
   * - Ctrl+click: Toggle selection of individual section
   * - Ctrl+click: Deselect individual section  
   * - Regular click on empty space: Clear selection and start drag select

   */
  const handleMouseDown = useCallback((e: React.MouseEvent, containerRef: React.RefObject<HTMLDivElement>) => {
    if (e.ctrlKey) {
      e.preventDefault();
    }

    if ((e.ctrlKey || e.shiftKey) && (e.target as HTMLElement).closest("[data-section-id]")) {
      const sectionElement = (e.target as HTMLElement).closest("[data-section-id]");
      if (sectionElement) {
        const sectionId = parseInt(sectionElement.getAttribute("data-section-id") || "0");

        if (e.ctrlKey) {
          setIntersectingSections((prev) =>
            prev.includes(sectionId) ? prev.filter((id) => id !== sectionId) : [...prev, sectionId]
          );
          return;
        }

        setIntersectingSections((prev) => prev.filter((id) => id !== sectionId));
        return;
      }
    }

    if (
      (e.target as HTMLElement).closest("[data-section-id]") ||
      (e.target as HTMLElement).closest(".draggable-icon")
    ) {
      return;
    }

    if (!(e.target as HTMLElement).closest("[data-section-id]")) {
      setIntersectingSections([]);
    }

    if (e.button !== 0) return;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const scrollTop = container.scrollTop;
    const scrollLeft = container.scrollLeft;

    const x = e.clientX - rect.left + scrollLeft;
    const y = e.clientY - rect.top + scrollTop;

    setIsSelecting(true);
    setSelectionStart({ x, y });
    setSelectionEnd({ x, y });
  }, []);

  /**
   * Handles mouse movement during drag selection
   * Updates the selection rectangle and identifies intersecting sections
   */
  const handleMouseMove = useCallback(
    (e: React.MouseEvent, containerRef: React.RefObject<HTMLDivElement>) => {
      if (!isSelecting) return;

      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const scrollTop = container.scrollTop;
      const scrollLeft = container.scrollLeft;

      const x = e.clientX - rect.left + scrollLeft;
      const y = e.clientY - rect.top + scrollTop;

      setSelectionEnd({ x, y });

      const selectionRect = {
        left: Math.min(selectionStart.x, x) + rect.left - scrollLeft,
        right: Math.max(selectionStart.x, x) + rect.left - scrollLeft,
        top: Math.min(selectionStart.y, y) + rect.top - scrollTop,
        bottom: Math.max(selectionStart.y, y) + rect.top - scrollTop
      };

      const currentlyIntersecting = sections
        .filter((section) => {
          const element = container.querySelector(`[data-section-id="${section.id}"]`);
          if (!element) return false;

          const elementRect = element.getBoundingClientRect();
          return !(
            elementRect.right < selectionRect.left ||
            elementRect.left > selectionRect.right ||
            elementRect.bottom < selectionRect.top ||
            elementRect.top > selectionRect.bottom
          );
        })
        .map((section) => section.id);

      setIntersectingSections(currentlyIntersecting);
    },
    [isSelecting, selectionStart.x, selectionStart.y, sections]
  );

  /**
   * Handles mouse up event to end the drag selection
   */
  const handleMouseUp = useCallback(() => {
    setIsSelecting(false);
  }, []);

  const selectionStyle = {
    left: Math.min(selectionStart.x, selectionEnd.x),
    top: Math.min(selectionStart.y, selectionEnd.y),
    width: Math.abs(selectionEnd.x - selectionStart.x),
    height: Math.abs(selectionEnd.y - selectionStart.y)
  };

  const isSectionMarkedForDrag = useCallback<ContentContextType["isSectionMarkedForDrag"]>(
    (sectionId) => {
      return intersectingSections.includes(sectionId);
    },
    [intersectingSections]
  );

  return {
    isSelecting,
    intersectingSections,
    selectionStyle,
    handleDrag,
    handleDragStart,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    setIntersectingSections,
    reorderSections,
    isSectionMarkedForDrag
  };
};
